// Libs
import React from 'react';
import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Components
import Reorder from '../../Button/ReorderButton/ReorderButton';
import Feather from '../../Feather';
import IconButton from '../../Button/IconButton/IconButton';

// Hooks
import useUser from '../../../hooks/useUser';
import useUI from '../../../hooks/useUI';
import useSharedKitActions from '../../../pages/Kits/hooks/useSharedKitActions';
import useSharedKitOrder from '../../../pages/Kits/hooks/useSharedKitOrder';

// Utils
import { getTotalQty } from '../../../pages/Kits/utils/calculations';

// Selectors
import { productsObjSelector } from '../../../store/selectors';

// Constants
import { ORDER_BUTTON_TITLE } from '../../../constants/sharedKits';

// Styles
import { useStyles } from './SharedKitAdaptiveCardStyles';

const SharedKitAdaptiveCard = ({ kit }) => {
  const { isKiteUser } = useUser();
  const { isMobileMode } = useUI();
  const productsObj = useSelector((state) => productsObjSelector(state));
  const { onViewDetailsClick, onDeleteClick, canOrder } =
    useSharedKitActions(kit);
  const { onOrderClick } = useSharedKitOrder();

  const { classes, cx } = useStyles({ isKiteUser, isMobileMode });

  return (
    <div className={classes.card} data-testid={'shared-kit-card'}>
      {isMobileMode ? (
        <>
          <Typography
            data-testid={'shared-kit-name-link-button'}
            variant="textMdSemibold"
            className={cx(classes.kitDetailsLink, classes.kitName)}
            title={kit.name}
            component={Link}
            to={`${kit.id}`}>
            {kit.name}
          </Typography>
          <Typography
            data-testid={'total-qty'}
            className={classes.mobileTotalQty}
            variant="textSmSemibold">
            Total Qty: {getTotalQty(kit, productsObj)}
          </Typography>
          <Reorder
            disabled={!canOrder}
            kit={kit}
            onClick={onOrderClick}
            title={ORDER_BUTTON_TITLE}
            className={classes.reorderButton}
            inTable
          />
        </>
      ) : (
        <>
          <Box display="flex" flexDirection="column" flexBasis="300px">
            <Typography
              data-testid={'shared-kit-name'}
              variant="h3"
              className={classes.kitName}
              title={kit.name}>
              {kit.name}
            </Typography>
            <IconButton
              data-testid={'shared-kit-details-button'}
              onClick={() => onViewDetailsClick(kit?.id)}
              endIcon={<ChevronRightIcon className={classes.detailsIcon} />}
              className={classes.flatIconButton}>
              Details
            </IconButton>
          </Box>
          <Box display="flex" justifyContent="center">
            <div data-testid={'total-qty'}>
              <Typography variant="h4">Total Qty:</Typography>
              <Typography
                className={classes.tabletTotalQtyValue}
                variant="textSmSemibold">
                {getTotalQty(kit, productsObj)}
              </Typography>
            </div>
          </Box>

          <Box display="flex" justifyContent="center">
            <Reorder
              disabled={!canOrder}
              kit={kit}
              onClick={onOrderClick}
              title={ORDER_BUTTON_TITLE}
              className={classes.reorderButtonWide}
              inTable
            />
          </Box>
        </>
      )}
      {kit.is_owner && (
        <span className={classes.removeIcon}>
          <Feather
            data-testid="shared-kit-remove-button"
            type="close"
            colorType="grey2"
            onClick={() => onDeleteClick(kit.id)}
          />
        </span>
      )}
    </div>
  );
};

export default SharedKitAdaptiveCard;
